import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const EMPTY = {
  client_id: "",
  technician_id: "",
  service_type: "correctivo",
  priority: "media",
  date: "",
  start_time: "",
  end_time: "",
  modality: "presencial",
  equipment: "",
  problem_description: "",
  diagnosis: "",
  status: "pendiente",
  observations: "",
};

const toLocalInput = (iso) => (iso ? String(iso).slice(0, 16) : "");

function Field({ label, children, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.15em] text-slate-500" data-testid={testId ? `${testId}-label` : undefined}>
        {label}
      </Label>
      {children}
    </div>
  );
}

export default function OrderForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [materials, setMaterials] = useState([]);
  const [clients, setClients] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(isEdit);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    if (user?.role === "admin") {
      api.get("/users/technicians").then((r) => setTechnicians(r.data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (!isEdit) return;
    api
      .get(`/orders/${id}`)
      .then(({ data }) => {
        setForm({
          client_id: data.client_id,
          technician_id: data.technician_id,
          service_type: data.service_type,
          priority: data.priority,
          date: data.date || "",
          start_time: toLocalInput(data.start_time),
          end_time: toLocalInput(data.end_time),
          modality: data.modality,
          equipment: data.equipment || "",
          problem_description: data.problem_description || "",
          diagnosis: data.diagnosis || "",
          status: data.status,
          observations: data.observations || "",
        });
        setMaterials(data.materials || []);
        setLoadingOrder(false);
      })
      .catch((e) => {
        toast.error(apiError(e, "No se pudo cargar la orden"));
        navigate("/");
      });
  }, [id, isEdit, navigate]);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const setMaterial = (index, key, value) => {
    setMaterials((list) => list.map((m, i) => (i === index ? { ...m, [key]: value } : m)));
  };

  const materialsTotal = materials.reduce(
    (sum, m) => sum + (parseFloat(m.quantity) || 0) * (parseFloat(m.unit_cost) || 0),
    0
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id) {
      toast.error("Seleccione un cliente");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        technician_id: form.technician_id || null,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        date: form.date || null,
        materials: materials
          .filter((m) => m.name.trim())
          .map((m) => ({
            name: m.name.trim(),
            quantity: parseFloat(m.quantity) || 0,
            unit_cost: parseFloat(m.unit_cost) || 0,
          })),
      };
      if (isEdit) {
        await api.put(`/orders/${id}`, payload);
        toast.success("Orden actualizada correctamente");
        navigate(`/orders/${id}`);
      } else {
        const { data } = await api.post("/orders", payload);
        toast.success(`Orden ${data.order_number} creada correctamente`);
        navigate(`/orders/${data.id}`);
      }
    } catch (err) {
      toast.error(apiError(err, "No se pudo guardar la orden"));
    } finally {
      setSaving(false);
    }
  };

  if (loadingOrder) {
    return <p className="text-sm text-slate-500" data-testid="order-form-loading">Cargando orden...</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-4xl space-y-6" data-testid="order-form">
      <Button type="button" variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1 text-slate-500" data-testid="order-form-back">
        <ArrowLeft className="h-4 w-4" /> Volver
      </Button>

      <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-5">
        <h2 className="font-heading text-lg font-bold text-slate-900">Información General</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Cliente *">
            <Select value={form.client_id} onValueChange={set("client_id")}>
              <SelectTrigger data-testid="order-client-select"><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {user?.role === "admin" && (
            <Field label="Técnico asignado">
              <Select value={form.technician_id || "none"} onValueChange={(v) => set("technician_id")(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="order-technician-select"><SelectValue placeholder="Asignar técnico" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar (yo)</SelectItem>
                  {technicians.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Fecha">
            <Input type="date" value={form.date} onChange={set("date")} data-testid="order-date-input" />
          </Field>
          <Field label="Tipo de servicio">
            <Select value={form.service_type} onValueChange={set("service_type")}>
              <SelectTrigger data-testid="order-service-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="correctivo">Correctivo</SelectItem>
                <SelectItem value="preventivo">Preventivo</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Prioridad">
            <Select value={form.priority} onValueChange={set("priority")}>
              <SelectTrigger data-testid="order-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alta">Alta</SelectItem>
                <SelectItem value="media">Media</SelectItem>
                <SelectItem value="baja">Baja</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Modalidad">
            <Select value={form.modality} onValueChange={set("modality")}>
              <SelectTrigger data-testid="order-modality-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="presencial">Presencial</SelectItem>
                <SelectItem value="remoto">Remoto</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Hora de inicio">
            <Input type="datetime-local" value={form.start_time} onChange={set("start_time")} data-testid="order-start-time-input" />
          </Field>
          <Field label="Hora de finalización">
            <Input type="datetime-local" value={form.end_time} onChange={set("end_time")} data-testid="order-end-time-input" />
          </Field>
          <Field label="Estado">
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="order-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pendiente">Pendiente</SelectItem>
                <SelectItem value="en_progreso">En progreso</SelectItem>
                <SelectItem value="completado">Completado</SelectItem>
                <SelectItem value="escalado">Escalado</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Equipo / Sistema afectado">
          <Input value={form.equipment} onChange={set("equipment")} placeholder="Ej: Servidor HP ProLiant, Red WiFi oficina..." data-testid="order-equipment-input" />
        </Field>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-4">
        <h2 className="font-heading text-lg font-bold text-slate-900">Trabajo Técnico</h2>
        <Field label="Descripción del problema">
          <Textarea rows={3} value={form.problem_description} onChange={set("problem_description")} data-testid="order-problem-input" />
        </Field>
        <Field label="Diagnóstico y trabajo realizado">
          <Textarea rows={3} value={form.diagnosis} onChange={set("diagnosis")} data-testid="order-diagnosis-input" />
        </Field>
        <Field label="Observaciones">
          <Textarea rows={2} value={form.observations} onChange={set("observations")} data-testid="order-observations-input" />
        </Field>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-slate-900">Materiales Utilizados</h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1"
            data-testid="add-material-button"
            onClick={() => setMaterials((l) => [...l, { name: "", quantity: 1, unit_cost: 0 }])}
          >
            <Plus className="h-4 w-4" /> Agregar
          </Button>
        </div>
        {materials.length === 0 ? (
          <p className="text-sm text-slate-400">Sin materiales registrados.</p>
        ) : (
          <div className="space-y-2">
            {materials.map((m, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`material-row-${i}`}>
                <Input
                  placeholder="Material"
                  className="min-w-[180px] flex-1"
                  value={m.name}
                  onChange={(e) => setMaterial(i, "name", e.target.value)}
                  data-testid={`material-name-${i}`}
                />
                <Input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Cant."
                  className="w-24"
                  value={m.quantity}
                  onChange={(e) => setMaterial(i, "quantity", e.target.value)}
                  data-testid={`material-qty-${i}`}
                />
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Costo unit."
                  className="w-28"
                  value={m.unit_cost}
                  onChange={(e) => setMaterial(i, "unit_cost", e.target.value)}
                  data-testid={`material-cost-${i}`}
                />
                <span className="w-24 text-right font-mono text-sm text-slate-600">
                  ${((parseFloat(m.quantity) || 0) * (parseFloat(m.unit_cost) || 0)).toFixed(2)}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setMaterials((l) => l.filter((_, j) => j !== i))}
                  data-testid={`material-remove-${i}`}
                  className="text-slate-400 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <p className="text-right text-sm font-semibold text-slate-900" data-testid="materials-total">
              Total materiales: <span className="font-mono">${materialsTotal.toFixed(2)}</span>
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="order-form-cancel">
          Cancelar
        </Button>
        <Button type="submit" disabled={saving} className="gap-2 bg-primary hover:bg-[#0353E9]" data-testid="order-form-submit-button">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {isEdit ? "Guardar cambios" : "Crear orden"}
        </Button>
      </div>
    </form>
  );
}
